import React, { useEffect, useRef, useState } from "react";
import { TopNavBar } from "../../components/TopNavBar";
import { Card, RectBtn } from "../../components/General";
import { PDFViewer } from "@react-pdf/renderer";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { useSelector } from "react-redux";
import { selectBuilding, selectDevice, selectRoom, selectUserData } from "../../features/store";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem, Select, Skeleton } from "@mui/material";
import { combineDateTime, delay, formatTime } from "../../utils/General";
import moment from "moment";
import {
    PieChart,
    Pie,
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Cell,
    Legend,
    CartesianGrid,
    Label,
} from "recharts";
import { toPng, toJpeg } from "html-to-image";
import { getFeedbackLog } from "../../apis/Feedback.Api";
import { useSnackbar } from "notistack";
import { FEEDBACK } from "../../configs/global";
import { getCumulativeCount } from "../../apis/PeopleCount.Api";
import { AiOutlineCheckCircle } from "react-icons/ai";
import EventSummaryReport from "./EventSummaryReport";
import PeopleCountReport from "./PeopleCountReport";
import FeedbackReport from "./FeedbackReport";
import { getPeopleCountLog } from "../../apis/PeopleCount.Api";
import { getSensorEventLog } from "../../apis/SensorEvent.Api";

const BUFFER_TIME = 1;
const FOOTFALL_FILTER = 12;
const Report = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [scrollTop, setScrollTop] = useState(0);
    const buildings = useSelector(selectBuilding);
    const rooms = useSelector(selectRoom);
    const devices = useSelector(selectDevice);
    // selected building id
    const [building, setBuilding] = useState(
        Object.keys(buildings).length
            ? Object.keys(buildings).sort((a, b) => {
                  return buildings[a].buildingName.localeCompare(buildings[b].buildingName);
              })[0]
            : ""
    );
    const [buildingError, setBuildingError] = useState("");
    // selected time range
    const [startDate, setStartDate] = useState(dayjs().subtract(1, "day"));
    const [endDate, setEndDate] = useState(dayjs());
    const [startTime, setStartTime] = useState(dayjs("00:00:00 am", "hh:mm:ss a"));
    const [endTime, setEndTime] = useState(dayjs("23:59:00 am", "hh:mm:ss a"));
    // generate pdf
    const [generating, setGenerating] = useState(false);
    const [generatingLinks, setGeneratingLinks] = useState(false);
    const [generatingComplaintPie, setGeneratingComplaintPie] = useState(false);
    const [generatingTotalBar, setGeneratingTotalBar] = useState(false);

    const [openPdf, setOpenPdf] = useState(false);
    const [displayBuilding, setDisplayBuilding] = useState(null);
    const [displayStart, setDisplayStart] = useState(null);
    const [displayEnd, setDisplayEnd] = useState(null);

    const [currentEventData, setCurrentEventData] = useState([]);
    const [peopleCountTableData, setPeopleCountTableData] = useState([]);

    const [feedbackTableData, setFeedbackTableData] = useState({
        good: 0,
        average: 0,
        poor: 0,
    });
    const [complaintPieData, setComplaintPieData] = useState({
        wet: 0,
        smell: 0,
        paper: 0,
        soap: 0,
    });
    const [complaintPieLink, setComplaintPieLink] = useState("");

    const [countDict, setCountDict] = useState({});
    const [totalPeopleCountData, setTotalPeopleCountData] = useState({});
    const [roomPeopleFlowLinks, setRoomPeopleFlowLinks] = useState([]);
    const [totalPeopleFlowLink, setTotalPeopleFlowLink] = useState("");
    // const [peopleCountRankLink, setPeopleCountRankLink] = useState([]);
    const [proceedPeopleFlowNum, setProceedPeopleFlowNum] = useState(0);

    const [noRoomException, setNoRoomException] = useState("");
    const [overviewItem, setOverviewItem] = useState("");

    async function generatePdf(callback) {
        setOverviewItem("");
        setBuildingError("");
        setNoRoomException("");
        if (building === "") {
            setBuildingError("Please select building.");
            return;
        }

        setOpenPdf(true);
        setGenerating(true);

        // header data
        let b = buildings[building].buildingName;
        setDisplayBuilding(b);
        let start = formatTime(startTime, startDate);
        setDisplayStart(start);
        let end = formatTime(endTime, endDate);
        setDisplayEnd(end);

        // confirm there is room in the building
        let roomsToQuery = [];
        for (let rid in rooms) {
            if (rooms[rid].buildingId !== building) continue;
            roomsToQuery.push(rid);
        }
        if (roomsToQuery.length === 0) {
            setNoRoomException("This building does not contain any room");
            setGenerating(false);
            setGeneratingComplaintPie(false);
            setGeneratingTotalBar(false);
            setGeneratingLinks(false);
            return;
        }

        await callback(roomsToQuery);

        setGenerating(false);
    }

    async function handlePeopleCount(roomsToQuery) {
        setGeneratingTotalBar(false);
        setGeneratingLinks(false);
        setCountDict({});
        setTotalPeopleCountData({});
        setPeopleCountTableData([]);

        setTimeout(async () => {
            setGeneratingTotalBar(true);
            setGeneratingLinks(true);
            const startDatetime = combineDateTime(startTime, startDate);
            const endDatetime = combineDateTime(endTime, endDate);
            let devicesToQuery = [];
            for (let did in devices) {
                if (did.toUpperCase().startsWith("P")) {
                    if (roomsToQuery.includes(devices[did].roomId)) {
                        devicesToQuery.push(did);
                    }
                }
            }
            let counts;
            if (devicesToQuery.length) {
                const res = await getPeopleCountLog({
                    device_id: JSON.stringify(devicesToQuery),
                    start_time: moment(startDatetime).format("YYMMDDHHmmssSSS"),
                    end_time: moment(endDatetime).format("YYMMDDHHmmssSSS"),
                });
                if (!res.data.indicator) {
                    enqueueSnackbar(res.data.message, { variant: "error" });
                    setGeneratingLinks(false);
                    return;
                }
                counts = res.data.message;
            } else {
                counts = [];
            }
            let cpy = {};
            for (let rid of roomsToQuery) {
                cpy[rid] = new Array(24).fill(0);
            }
            let totalData = {};
            let left = new Date(startDatetime);
            while (left < endDatetime) {
                totalData[moment(left).format("MM/DD/YY")] = 0;
                left.setDate(left.getDate() + 1);
            }

            let noPeople = true;
            for (let count of counts) {
                let rid = count.room_id;
                let time = moment(count.update_time, "YYMMDDHHmmssSSS").toDate();
                let hour = time.getHours();
                let date = moment(time).format("MM/DD/YY");
                cpy[rid][hour] += count.entry;
                totalData[date] += count.entry;
                if (count.entry !== 0) {
                    noPeople = false;
                }
            }

            setCountDict(cpy);
            if (noPeople) {
                setTotalPeopleCountData([]);
            } else {
                setTotalPeopleCountData(totalData);
            }

            let r = await getCumulativeCount({
                room_id: JSON.stringify(roomsToQuery),
                start_time: moment(startDatetime).format("YYMMDDHHmmssSSS"),
                end_time: moment(endDatetime).format("YYMMDDHHmmssSSS"),
            });
            if (!r.data.indicator) {
                enqueueSnackbar(r.data.message, { variant: "error" });
                return;
            }
            let tableData = r.data.message
                .sort((obj1, obj2) => {
                    return obj2.cumulative_count - obj1.cumulative_count;
                })
                .map((countObj, i) => ({
                    Rank: i + 1,
                    RoomId: countObj.room_id,
                    Room: countObj.room_name,
                    "Cumulative Count": countObj.cumulative_count,
                }));

            setPeopleCountTableData(tableData);
        }, 100);
    }

    async function handleCurrentEvent(roomsToQuery) {
        const startDatetime = combineDateTime(startTime, startDate);
        const endDatetime = combineDateTime(endTime, endDate);
        let r = await getSensorEventLog({
            room_id: JSON.stringify(roomsToQuery),
            start_time: moment(startDatetime).format("YYMMDDHHmmssSSS"),
            end_time: moment(endDatetime).format("YYMMDDHHmmssSSS"),
        });

        if (!r.data.indicator) {
            enqueueSnackbar(r.data.message, { variant: "error" });
            return;
        }

        let eventSummary = {};
        for (let event of r.data.message) {
            let rid = event.room_id;
            let roomName = event.room_name;
            let type = event.sensor_type;
            if (rid in eventSummary) {
                eventSummary[rid][type] += 1;
                eventSummary[rid].Total += 1;
            } else {
                eventSummary[rid] = {
                    Room: roomName,
                    Ammonia: 0,
                    "Paper Roll": 0,
                    "Hand Towel": 0,
                    Soap: 0,
                    Total: 0,
                };
                eventSummary[rid][type] += 1;
                eventSummary[rid].Total += 1;
            }
        }

        let ranked = Object.keys(eventSummary)
            .sort((rid1, rid2) => {
                return eventSummary[rid2].Total - eventSummary[rid1].Total;
            })
            .map((rid, i) => ({ Rank: i + 1, ...eventSummary[rid] }));
        setCurrentEventData(ranked);
        setOverviewItem("Current Event");
    }

    async function handleFeedback(roomsToQuery) {
        setGeneratingComplaintPie(true);
        const startDatetime = combineDateTime(startTime, startDate);
        const endDatetime = combineDateTime(endTime, endDate);

        let r = await getFeedbackLog({
            room_id: JSON.stringify(roomsToQuery),
            start_time: moment(startDatetime).format("YYMMDDHHmmssSSS"),
            end_time: moment(endDatetime).format("YYMMDDHHmmssSSS"),
        });
        if (!r.data.indicator) {
            enqueueSnackbar(r.data.message, { variant: "error" });
            return;
        }
        let poorCount = 0;
        let averageCount = 0;
        let goodCount = 0;
        let complaints = {
            wet: 0,
            smell: 0,
            paper: 0,
            soap: 0,
        };
        for (let msg of r.data.message) {
            if (msg.rating === 1) {
                poorCount += 1;

                if (msg.message === FEEDBACK.SMELL) {
                    complaints.smell += 1;
                } else if (msg.message === FEEDBACK.SOAP) {
                    complaints.soap += 1;
                } else if (msg.message === FEEDBACK.FLOOR) {
                    complaints.wet += 1;
                } else if (msg.message === FEEDBACK.PAPER) {
                    complaints.paper += 1;
                }
            } else if (msg.rating === 2) {
                averageCount += 1;
            } else if (msg.rating === 3) {
                goodCount += 1;
            }
        }
        setFeedbackTableData({
            good: goodCount,
            average: averageCount,
            poor: poorCount,
        });
        setComplaintPieData(complaints);
    }
    return (
        <div
            className="report"
            onScroll={(e) => {
                setScrollTop(e.currentTarget.scrollTop);
            }}
        >
            <TopNavBar hideDropDown={true} shadow={scrollTop !== 0}></TopNavBar>

            <div className="report-view-controller">
                <div className={`report-content`}>
                    <h3> Reports </h3>
                    <Card>
                        <div className="report-card-content">
                            <div />
                            <FormControl error={buildingError !== ""}>
                                <InputLabel id="select-building">Building</InputLabel>

                                <Select
                                    label="Building"
                                    labelId="select-building"
                                    id="select-building"
                                    value={building}
                                    onChange={(e) => {
                                        setBuilding(e.target.value);
                                    }}
                                >
                                    {Object.keys(buildings)
                                        .sort((a, b) => {
                                            return buildings[a].buildingName.localeCompare(buildings[b].buildingName);
                                        })
                                        .map((bid, i) => {
                                            let displayName = buildings[bid].buildingName;
                                            return (
                                                <MenuItem value={bid} key={bid}>
                                                    {displayName}
                                                </MenuItem>
                                            );
                                        })}
                                </Select>
                                <FormHelperText>{buildingError}</FormHelperText>
                            </FormControl>
                            <div className="report-divider" />

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div className="report-option-row">
                                    <DatePicker
                                        label={"Start Date"}
                                        minDate={dayjs("08-31-2022", "MM-DD-YYYY")}
                                        maxDate={endDate}
                                        showDaysOutsideCurrentMonth
                                        onChange={(v) => {
                                            setStartDate(v);
                                        }}
                                        sx={{ flex: 1 }}
                                        value={startDate}
                                    />
                                    <TimePicker
                                        views={["hours", "minutes"]}
                                        minutesStep={1}
                                        onChange={(v) => {
                                            setStartTime(v);
                                        }}
                                        label={"Start Time"}
                                        sx={{ flex: 1 }}
                                        value={startTime}
                                    />
                                </div>
                                <div className="report-option-row">
                                    <DatePicker
                                        minDate={startDate}
                                        maxDate={dayjs()}
                                        showDaysOutsideCurrentMonth
                                        onChange={(v) => {
                                            setEndDate(v);
                                        }}
                                        label={"End Date"}
                                        sx={{ flex: 1 }}
                                        value={endDate}
                                    />
                                    <TimePicker
                                        views={["hours", "minutes"]}
                                        minutesStep={1}
                                        onChange={(v) => {
                                            setEndTime(v);
                                        }}
                                        label={"End Time"}
                                        sx={{ flex: 1 }}
                                        value={endTime}
                                    />
                                </div>
                            </LocalizationProvider>

                            <div className="report-btn-wrapper">
                                <RectBtn
                                    onClick={() => generatePdf(handlePeopleCount)}
                                    disable={
                                        generating || generatingTotalBar || generatingLinks || generatingComplaintPie
                                    }
                                >
                                    Footfall
                                </RectBtn>
                                <RectBtn
                                    onClick={() => generatePdf(handleCurrentEvent)}
                                    disable={
                                        generating || generatingTotalBar || generatingLinks || generatingComplaintPie
                                    }
                                >
                                    Event Summary
                                </RectBtn>
                                <RectBtn
                                    onClick={() => generatePdf(handleFeedback)}
                                    disable={
                                        generating || generatingTotalBar || generatingLinks || generatingComplaintPie
                                    }
                                >
                                    Feedback
                                </RectBtn>
                            </div>

                            <div className="progress-group">
                                {(generating || generatingComplaintPie || generatingLinks || generatingTotalBar) && (
                                    <>
                                        <p className="progress-hint">Generating pdf. Please don't leave this page.</p>

                                        {/*Object.entries({
                      "Packaging pdf...": !generating,
                      "Collecting feedback data...": !generatingComplaintPie,
                      "Calculating footfall...": !generatingTotalBar,
                      "Analyzing room data...": !generatingLinks,
                    }).map((entry) => {
                      let key = entry[0];
                      let value = entry[1];
                      return (
                        <ProgressIndicator
                          text={key}
                          indicator={value}
                          key={JSON.stringify(entry)}
                        />
                      );
                    })*/}

                                        {generating && (
                                            <ProgressIndicator text={"Packaging pdf..."} indicator={!generating} />
                                        )}

                                        {generatingTotalBar && (
                                            <ProgressIndicator
                                                text={"Calculating Footfall..."}
                                                indicator={!generatingTotalBar}
                                            />
                                        )}
                                        {generatingLinks && Object.keys(countDict).length > 0 && (
                                            <ProgressIndicator
                                                text={`Analyzing room data... (${proceedPeopleFlowNum}/${
                                                    Object.keys(countDict).length
                                                })`}
                                                indicator={!generatingLinks}
                                            />
                                        )}
                                        {generatingComplaintPie && (
                                            <ProgressIndicator
                                                text={"Collecting feedback data..."}
                                                indicator={!generatingComplaintPie}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
                {openPdf && (
                    <div className={`report-pdf-wrapper`}>
                        {generating || generatingLinks || generatingTotalBar || generatingComplaintPie ? (
                            <Skeleton variant="rounded" width={"100%"} height={"100%"} />
                        ) : (
                            <PDFViewer className="pdf-frame" width={"100%"} height={"100%"}>
                                {overviewItem === "Footfall" &&
                                !generatingTotalBar &&
                                !generatingLinks &&
                                !generating ? (
                                    <PeopleCountReport
                                        footfallFilter={FOOTFALL_FILTER}
                                        buildingName={displayBuilding}
                                        start={displayStart}
                                        end={displayEnd}
                                        peopleCountTableData={peopleCountTableData}
                                        roomNameList={Object.keys(countDict)
                                            .sort(
                                                (a, b) =>
                                                    a.split("-")[1]?.match(/\d+/)[0] - b.split("-")[1]?.match(/\d+/)[0]
                                            )
                                            .map((rid) => rooms[rid].roomName)}
                                        roomPeopleFlowLinks={roomPeopleFlowLinks}
                                        totalPeopleFlowLink={totalPeopleFlowLink}
                                    />
                                ) : overviewItem === "Current Event" && !generating ? (
                                    <EventSummaryReport
                                        buildingName={displayBuilding}
                                        start={displayStart}
                                        end={displayEnd}
                                        contentData={currentEventData}
                                    />
                                ) : overviewItem === "Feedback" && !generatingComplaintPie && !generating ? (
                                    <FeedbackReport
                                        buildingName={displayBuilding}
                                        start={displayStart}
                                        end={displayEnd}
                                        feedbackTableData={feedbackTableData}
                                        complaintPieLink={complaintPieLink}
                                    />
                                ) : (
                                    <></>
                                )}
                            </PDFViewer>
                        )}
                    </div>
                )}
            </div>

            <div className="hidden">
                <PeopleFlowChart
                    countDict={countDict}
                    setCountDict={setCountDict}
                    setRoomPeopleFlowLinks={setRoomPeopleFlowLinks}
                    setGeneratingLinks={setGeneratingLinks}
                    generatingLinks={generatingLinks}
                    setOverviewItem={setOverviewItem}
                    proceedPeopleFlowNum={proceedPeopleFlowNum}
                    setProceedPeopleFlowNum={setProceedPeopleFlowNum}
                    peopleCountTableData={peopleCountTableData}
                />
                <TotalPeopleFlowChart
                    dataDict={totalPeopleCountData}
                    setTotalPeopleFlowLink={setTotalPeopleFlowLink}
                    generating={generatingTotalBar}
                    setGenerating={setGeneratingTotalBar}
                    setOverviewItem={setOverviewItem}
                />
                <ComplaintPieChart
                    dataDict={complaintPieData}
                    setComplaintPieLink={setComplaintPieLink}
                    generating={generatingComplaintPie}
                    setGenerating={setGeneratingComplaintPie}
                    setOverviewItem={setOverviewItem}
                />
            </div>
        </div>
    );
};

export default Report;

/**
 * expected dataDict = {
 *  wet: 0,
 *  smell: 0,
 *  paper: 0,
 *  soap: 0
 * }
 *
 */
const ComplaintPieChart = ({ dataDict, setComplaintPieLink, generating, setGenerating, setOverviewItem }) => {
    const colors = ["#574477", "#b34a80", "#f7625a", "#ff9d00"];
    const dataAbbrev = ["Wet", "Smell", "Paper", "Soap"];
    const [total, setTotal] = useState(0);
    const [data, setData] = useState([]);
    const complaintPieRef = useRef(null);
    useEffect(() => {
        if (!generating) return;
        let t = 0;
        for (let k in dataDict) {
            t += dataDict[k];
        }
        setTotal(t);
        setData([
            { name: "Wet Floor (Wet)", value: dataDict["wet"] },
            { name: "Ammonia level High (Smell)", value: dataDict["smell"] },
            { name: "No Paper Roll (Paper)", value: dataDict["paper"] },
            { name: "No Hand Soap (Soap)", value: dataDict["soap"] },
        ]);
    }, [dataDict]);

    useEffect(() => {
        if (!generating) return;
        handleScreenShot();
    }, [data]);

    async function handleScreenShot() {
        // take screenshot
        if (!complaintPieRef.current) {
            setGenerating(false);
            return;
        }
        await delay(BUFFER_TIME);
        // let link = await toPng(complaintPieRef.current);
        let link = await toJpeg(complaintPieRef.current, { quality: 0.8 });
        setComplaintPieLink(link);
        setGenerating(false);
        setTimeout(() => {
            setOverviewItem("Feedback");
        }, 500);
    }
    return (
        <div className="report-pie-chart-wrapper" ref={complaintPieRef}>
            {dataDict["wet"] === 0 && dataDict["smell"] === 0 && dataDict["paper"] === 0 && dataDict["soap"] === 0 ? (
                <p className="empty">No complaint detected</p>
            ) : (
                <ResponsiveContainer>
                    <PieChart>
                        <Pie
                            dataKey="value"
                            isAnimationActive={false}
                            data={data}
                            cx="50%"
                            cy="45%"
                            outerRadius={"60%"}
                            label={({ cx, cy, midAngle, innerRadius, outerRadius, value, index }) => {
                                const RADIAN = Math.PI / 180;
                                const radius = 25 + innerRadius + (outerRadius - innerRadius);
                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                                return data[index].value !== 0 ? (
                                    <text
                                        x={x}
                                        y={y}
                                        fill={colors[index % colors.length]}
                                        textAnchor={x > cx ? "start" : "end"}
                                        dominantBaseline="central"
                                    >
                                        {dataAbbrev[index]}: {value} ({Math.round((data[index].value / total) * 100)}%)
                                    </text>
                                ) : (
                                    <></>
                                );
                            }}
                        >
                            {[0, 1, 2, 3].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                            ))}
                        </Pie>
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

const PeopleFlowChart = ({
    countDict,
    peopleCountTableData,
    setRoomPeopleFlowLinks,
    setGeneratingLinks,
    generatingLinks,
    setOverviewItem,
    setProceedPeopleFlowNum,
}) => {
    const [keyList, setKeyList] = useState([]);
    const [displayData, setDisplayData] = useState([]);
    const [localLinks, setLocalLinks] = useState([]);
    const peopleFlowRef = useRef(null);
    const [loadDataFlag, setLoadDataFlag] = useState(false);

    useEffect(() => {
        if (!generatingLinks) return;
        setLocalLinks([]);
        // setNum(Object.keys(countDict).length);
        setLoadDataFlag(Object.keys(countDict).length > 0 && peopleCountTableData.length > 0);
    }, [countDict, peopleCountTableData]);

    useEffect(() => {
        if (!generatingLinks) return;
        if (!loadDataFlag) {
            setRoomPeopleFlowLinks(localLinks);
            setGeneratingLinks(false);
            setOverviewItem("Footfall");
            setProceedPeopleFlowNum(0);
            return;
        }
        const validKeys = peopleCountTableData
            .filter((data) => data.Rank <= FOOTFALL_FILTER)
            .map((data) => data.RoomId);
        let key = Object.keys(countDict)
            .filter((k) => validKeys.includes(k))
            .sort()
            .reverse();

        // let key = Object.keys(countDict).sort(
        //   (a, b) =>
        //     b.split("-")[1].match(/\d+/)[0] - a.split("-")[1].match(/\d+/)[0]
        // )[num - 1];
        // let countList = countDict[key];

        let data = [];
        let sum = 0;
        let keylen = key.length;

        for (let i = 0; i < 24; i++) {
            const time = i.toString().padStart(2, "0") + ":00";

            let tempdict = { time: time, range: i };

            sum = 0;

            for (let j = 0; j < key.length; j++) {
                tempdict[key[j]] = countDict[key[j]][i];
                sum += countDict[key[j]][i];
            }

            tempdict["avg"] = sum / keylen;

            data.push(tempdict);

            // data.push({ time: time, count: countList[i], range: i });
        }
        key = [...key, "avg"];

        setDisplayData(data);
        setKeyList(key);
        // setDisplayDataList([...displayDataList, data]);
        console.log("data: ", data, "count: ", countDict);
    }, [loadDataFlag]);

    useEffect(() => {
        if (!generatingLinks) return;
        handleScreenShot();
    }, [displayData]);

    async function handleScreenShot() {
        const startTime = moment();
        if (!peopleFlowRef.current) return;
        await delay(BUFFER_TIME);
        // let pLink = await toPng(peopleFlowRef.current);
        let pLink = await toJpeg(peopleFlowRef.current, { quality: 0.9 });
        setLocalLinks([...localLinks, pLink]);
        setLoadDataFlag(false);
        setProceedPeopleFlowNum((prev) => prev + 1);
        console.log("Time taken to generate link: ", moment().diff(startTime, "seconds"));
    }

    function getRandomColor() {
        var letters = "0123456789ABCDEF";
        var color = "#";
        for (var i = 0; i < 6; i++) {
            if (i % 2 === 0) {
                // Use darker shades by limiting the first digit of each color channel
                color += letters[Math.floor(Math.random() * 8)]; // Only use values 0-7 for darker shades
            } else {
                color += letters[Math.floor(Math.random() * 16)];
            }
        }

        // var letters = "0123456789ABCDEF";
        // var color = "#";
        // for (var i = 0; i < 6; i++) {
        //   color += letters[Math.floor(Math.random() * 16)];
        // }
        return color;
        // let n = num - 1;
        // setNum(n);
        // setProceedPeopleFlowNum((prev) => prev + 1);
        // console.log(
        //   "Time taken to generate link: ",
        //   moment().diff(startTime, "seconds")
        // );
    }

    return (
        <div className={`report-bar-chart-wrapper ${!displayData.length && "shorten"}`} ref={peopleFlowRef}>
            {displayData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={displayData}
                        margin={{
                            top: 2,
                            right: 10,
                            left: 10,
                            bottom: 2,
                        }}
                        barCategoryGap={1}
                    >
                        <XAxis dataKey="range" hide />
                        <XAxis
                            dataKey="time"
                            scale={"band"}
                            xAxisId="values"
                            padding={{ right: 0, left: 0 }}
                            tick={{ fontSize: 12 }}
                            interval={"equidistantPreserveStart"}
                        />

                        <CartesianGrid strokeDasharray="3 3" vertical={0} />
                        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />

                        <Legend />
                        {keyList.map((kname) => (
                            <Line dataKey={kname} stroke={getRandomColor()} isAnimationActive={false} />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            ) : (
                <p className="empty">No people detected</p>
            )}
        </div>
    );
};

const TotalPeopleFlowChart = ({ dataDict, setTotalPeopleFlowLink, generating, setGenerating, setOverviewItem }) => {
    const [data, setData] = useState([]);
    const ref = useRef(null);

    useEffect(() => {
        if (!generating) return;
        let formatData = Object.keys(dataDict).map((date) => ({
            time: date,
            count: dataDict[date],
        }));
        setData(formatData);
    }, [dataDict]);

    useEffect(() => {
        if (!generating) return;
        handleScreenShot();
    }, [data]);

    async function handleScreenShot() {
        // take screenshot
        if (!ref.current) {
            setGenerating(false);
            return;
        }
        await delay(BUFFER_TIME);
        // let link = await toPng(ref.current);
        let link = await toJpeg(ref.current, { quality: 0.4 });
        setTotalPeopleFlowLink(link);
        setGenerating(false);
        setOverviewItem("Footfall");
    }

    return (
        <div className={`report-bar-chart-wrapper ${!data.length && "shorten"}`} ref={ref}>
            {data.length ? (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        margin={{
                            top: 2,
                            right: 40,
                            left: 5,
                            bottom: 70,
                        }}
                        barCategoryGap={1}
                        isAnimationActive={false}
                    >
                        <XAxis
                            dataKey="time"
                            padding={{ top: 60, right: 0, left: 0 }}
                            tick={{ fontSize: 14 }}
                            interval={"equidistantPreserveStart"}
                            angle={45}
                            textAnchor="start"
                        >
                            <Label value="Date (MM/DD/YY)" offset={40} position="bottom" />
                        </XAxis>

                        <CartesianGrid strokeDasharray="3 3" vertical={0} />
                        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />

                        <Legend verticalAlign="top" />
                        <Bar dataKey="count" fill={"#122a65"} isAnimationActive={false} />
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <p className="empty">No people detected</p>
            )}
        </div>
    );
};

const ProgressIndicator = ({ text, indicator }) => {
    return (
        <div className="progress-indicator">
            {indicator ? <AiOutlineCheckCircle size="1.25rem" color="#499e93" /> : <CircularProgress size="1rem" />}

            {text}
        </div>
    );
};
